import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useAdd from "../../../../api/useAdd";

const AddPaymentDialog = ({ open, onClose, treatment, token, refetch }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    amount: "",
    currency: 1, // Default to AFN
    exchangeRate: "",
  });
  const [errors, setErrors] = useState({});

  const { handleAdd, loading } = useAdd("dental-payments", token);

  const currencyOptions = [
    { value: 1, label: "؋ AFN", symbol: "؋" },
    { value: 2, label: "$ USD", symbol: "$" },
    { value: 3, label: "€ EUR", symbol: "€" },
  ];

  const getCurrencySymbol = (currency) => {
    const option = currencyOptions.find((opt) => opt.value === currency);
    return option ? option.symbol : "؋";
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t("dental.amountRequired");
    }

    // Validate exchange rate for non-AFN currencies
    if (formData.currency !== 1) {
      if (!formData.exchangeRate || parseFloat(formData.exchangeRate) <= 0) {
        newErrors.exchangeRate = t("dental.exchangeRateRequired");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const paymentData = {
      treatment: treatment.id,
      amount: parseFloat(formData.amount),
      currency: formData.currency,
    };

    // Update treatment exchange rate if provided
    if (formData.exchangeRate && formData.currency !== 1) {
      // This would need to be handled in the backend
      paymentData.exchangeRate = parseFloat(formData.exchangeRate);
    }

    try {
      await handleAdd(paymentData);
      refetch(); // Refresh the treatment data
      onClose();
      setFormData({ amount: "", currency: 1, exchangeRate: "" });
      setErrors({});
    } catch (error) {
      console.error("Error adding payment:", error);
    }
  };

  const handleClose = () => {
    onClose();
    setFormData({ amount: "", currency: 1, exchangeRate: "" });
    setErrors({});
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("dental.addPayment")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t("dental.patient")}:</strong> {treatment?.patient_name}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t("dental.totalCost")}:</strong> {treatment?.total_cost}{" "}
              ؋
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t("dental.balanceDue")}:</strong>{" "}
              {treatment?.balance_due} ؋
            </Typography>
          </Box>

          <TextField
            fullWidth
            label={t("dental.amount")}
            type="number"
            value={formData.amount}
            onChange={(e) => handleChange("amount", e.target.value)}
            error={!!errors.amount}
            helperText={errors.amount}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {getCurrencySymbol(formData.currency)}
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t("dental.currency")}</InputLabel>
            <Select
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              label={t("dental.currency")}
            >
              {currencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.currency !== 1 && (
            <TextField
              fullWidth
              label={t("dental.exchangeRate")}
              type="number"
              value={formData.exchangeRate}
              onChange={(e) => handleChange("exchangeRate", e.target.value)}
              error={!!errors.exchangeRate}
              helperText={errors.exchangeRate || t("dental.exchangeRateHelp")}
              InputProps={{
                endAdornment: <InputAdornment position="end">؋</InputAdornment>,
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t("dental.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? t("dental.saving") : t("dental.addPayment")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddPaymentDialog;
